import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import VendorCard from '@/components/VendorCard';
import { Button } from '@/components/ui/button';
import { Heart, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchFavorites();
    }
  }, [isAuthenticated]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24" data-testid="dashboard-page">
      <div className="container-wedding">
        {/* Header */}
        <div className="mb-12">
          <p className="caption mb-4">Your Wedding Planning</p>
          <h1 className="heading-xl mb-2">Welcome, {user?.name}</h1>
          <p className="text-stone-600">Manage your favourite vendors and wedding planning.</p>
        </div>

        {/* Favorites Section */}
        <section>
          <div className="flex items-center gap-3 mb-8">
            <Heart className="h-6 w-6 text-[#D4AF37]" />
            <h2 className="heading-md">Your Favourites</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-[#5C0120]" />
            </div>
          ) : favorites.length === 0 ? (
            <div className="bg-white p-12 rounded-sm border border-stone-100 card-shadow text-center">
              <Heart className="h-16 w-16 text-stone-200 mx-auto mb-4" />
              <h3 className="heading-md mb-2">No favourites yet</h3>
              <p className="text-stone-500 mb-6">
                Start exploring vendors and save your favourites to compare later.
              </p>
              <Link to="/vendors">
                <Button className="btn-primary btn-lift">
                  Browse Vendors
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {favorites.map((vendor, index) => (
                <div
                  key={vendor.id}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <VendorCard vendor={vendor} onFavoriteChange={fetchFavorites} />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
